import {function_delay, function_throttle, mobile_friendly_resize_callback, set_class, get_ios_version} from 'lib/utilities';

export default function VideoPlayer(player) {
    const elements = {};
    let togglingPlay = false;

    function constructor(instance) {
        destroy_prev_instance();
        find_elements();

        disable_autoplay_ios();

        load_video_source();
        add_events();

        update_is_playing_class();
        update_is_muted_class();

        resize_video();

        add_instance_property(instance);

        return instance;
    }

    // Throttled Functions

    let hide_controls_delayed = function_delay(2000, hide_controls);

    let resize_video_throttled = function_throttle(250, resize_video);
    let resize_video_throttled_mobile_friendly = mobile_friendly_resize_callback(resize_video_throttled);

    // Init methods

    function add_instance_property(instance) {
        player.__videoPlayerInstance = instance;
    }

    function remove_instance_property() {
        player.__videoPlayerInstance = void 0;

        delete player.__videoPlayerInstance;
    }

    function destroy_prev_instance() {
        if (player.__videoPlayerInstance) {
            player.__videoPlayerInstance.destroy();
        }
    }

    function add_events() {
        window.addEventListener('resize', on_window_resize);

        elements.player.addEventListener('mouseenter', on_mouseenter);
        elements.player.addEventListener('mousemove', on_mousemove);
        elements.player.addEventListener('mouseleave', on_mouseleave);
        elements.video.addEventListener('playing', on_playing);
        elements.video.addEventListener('pause', on_paused);
        elements.video.addEventListener('ended', on_ended);
        elements.video.addEventListener('volumechange', on_volume_change);

        if (elements.controls) {
            elements.controls.addEventListener('click', on_controls_click);
            elements.play.addEventListener('click', on_play_click);
            elements.mute.addEventListener('click', on_mute_click);
            elements.rewind.addEventListener('click', on_rewind_click);
        }
    }

    function remove_events() {
        window.removeEventListener('resize', on_window_resize);

        elements.player.removeEventListener('mouseenter', on_mouseenter);
        elements.player.removeEventListener('mousemove', on_mousemove);
        elements.player.removeEventListener('mouseleave', on_mouseleave);
        elements.video.removeEventListener('playing', on_playing);
        elements.video.removeEventListener('pause', on_paused);
        elements.video.removeEventListener('ended', on_ended);
        elements.video.removeEventListener('volumechange', on_volume_change);

        if (elements.controls) {
            elements.controls.removeEventListener('click', on_controls_click);
            elements.play.removeEventListener('click', on_play_click);
            elements.mute.removeEventListener('click', on_mute_click);
            elements.rewind.removeEventListener('click', on_rewind_click);
        }
    }

    function get_body_margin_top() {
        let style = getComputedStyle(document.body);

        return parseInt(style.marginTop);
    }

    function resize_banner_video() {
        let windowWidth = window.innerWidth;
        let windowHeight = window.innerHeight;
        let videoHeight;

        // If the video is set to crop only in landscape, then as soon as we enter portrait, force the player aspect
        // ratio the standard 16:9 aspect-ratio.
        if (windowWidth < windowHeight && elements.player.classList.contains('crop-landscape-only')) {
            videoHeight = 9/16 * windowWidth;
        } else {
            // Subtract body margin-top to account for the fixed header
            videoHeight = windowHeight - get_body_margin_top();
        }

        elements.player.style.height = videoHeight + 'px';
    }

    function resize_video() {
        if (elements.player.classList.contains('banner-video')) {
            resize_banner_video();
        }
    }

    function find_elements() {
        elements.player = player;
        elements.video = player.querySelector('video');
        elements.source = player.querySelector('video source');
        elements.controls = player.querySelector('.controls');

        if (elements.controls) {
            elements.play = elements.controls.querySelector('.play-button');
            elements.mute = player.querySelector('.mute-button');
            elements.rewind = player.querySelector('.rewind-button');
        }
    }

    // Event Listeners

    function on_window_resize() {
        resize_video_throttled_mobile_friendly();
    }

    function on_play_click() {
        toggle_play();
    }

    function on_mute_click() {
        toggle_mute();
    }

    function on_rewind_click() {
        rewind_video();
    }

    function on_mouseenter() {
        show_controls();
        hide_controls_delayed();
    }

    function on_mousemove() {
        show_controls();
        hide_controls_delayed();
    }

    function on_mouseleave() {
        hide_controls();
    }

    function on_controls_click() {
        show_controls();
        hide_controls_delayed();
    }

    function on_playing() {
        update_is_playing_class();
        update_is_ended_class();
        update_show_on_initial_play_class();
        hide_controls_delayed();
    }

    function on_paused() {
        update_is_playing_class();
    }

    function on_ended() {
        update_is_playing_class();
        update_is_ended_class();
    }

    function on_volume_change() {
        update_is_muted_class();
    }

    // Private Methods

    function resolve_responsive_source() {
        let lazySrc = elements.source.getAttribute('data-src');
        let desktopSrc = elements.source.getAttribute('data-src-desktop');
        let mobileSrc = elements.source.getAttribute('data-src-mobile');

        if (desktopSrc && mobileSrc) {
            if (window.innerWidth < 1280) {
                return mobileSrc;
            } else {
                return desktopSrc;
            }
        } else if (desktopSrc) {
            return desktopSrc;
        } else if (mobileSrc) {
            return mobileSrc;
        } else {
            return lazySrc;
        }
    }

    function resolve_video_source() {
        if (elements.source.src) {
            return elements.source.src;
        }

        return resolve_responsive_source();
    }

    function load_video_source() {
        let src = resolve_video_source();

        if (! src) {
            console.error('[VideoPlayer]: Could not resolve video source', elements.video);
        } else if (elements.source.src !== src) {
            elements.source.src = src;

            append_new_video_source();
        }
    }

    function append_new_video_source() {
        let source = elements.source.cloneNode();

        elements.video.removeChild(elements.source);

        elements.source = source;
        elements.video.appendChild(source);
    }

    function update_show_on_initial_play_class() {
        if (elements.player.classList.contains('show-on-initial-play')) {
            setTimeout(() => elements.player.classList.remove('show-on-initial-play'), 1000);
        }
    }

    function update_is_playing_class() {
        set_class(elements.player, 'is-playing', ! elements.video.paused);
    }

    function update_is_ended_class() {
        set_class(elements.player, 'is-ended', elements.video.ended);
    }

    function update_is_muted_class() {
        set_class(elements.player, 'is-muted', elements.video.muted);
    }

    function reset_elements() {
        elements.player.classList.remove('is-muted');
        elements.player.classList.remove('is-playing');
        elements.player.classList.remove('show-controls');
    }

    /**
     * On iOS < 13.5, you can autoplay a video without sound, but then even with a user interaction, you cannot unmute
     * the video.
     */
    function disable_autoplay_ios() {
        if (! browser_supports_autoplay_with_user_triggered_unmute()) {
            disable_autoplay_and_unmute();
        }
    }

    function browser_supports_autoplay_with_user_triggered_unmute() {
        const v = get_ios_version();

        return ! (v[0] <= 13 && v[1] < 5);
    }

    function disable_autoplay_and_unmute() {
        elements.video.removeAttribute('autoplay');
        elements.video.removeAttribute('muted');
        elements.video.muted = false;
    }

    // Player Functions

    function hide_controls() {
        elements.player.classList.remove('show-controls');
    }

    function show_controls() {
        elements.player.classList.add('show-controls');
    }

    async function toggle_play() {
        // HTMLVideoElement.prototype.play is asynchronous, don't pause whilst we're still waiting for a play to go
        // through.
        if (togglingPlay) {
            return;
        }

        togglingPlay = true;

        try {
            if (elements.video.paused) {
                const promise = elements.video.play();

                // Older browsers don't return a promise
                if (promise) {
                    await promise;
                }
            } else {
                elements.video.pause();
            }
        } finally {
            togglingPlay = false;
        }
    }

    function play() {
        return elements.video.play();
    }

    function pause() {
        elements.video.pause();
    }

    function toggle_mute() {
        if (elements.video.muted) {
            unmute();
        } else {
            mute();
        }
    }

    function mute() {
        elements.video.muted = true;
        elements.video.volume = 0;
    }

    function unmute() {
        elements.video.muted = false;
        elements.video.volume = 1;
    }

    function rewind_video() {
        elements.video.currentTime = 0;
    }

    function destroy() {
        remove_events();
        reset_elements();
        remove_instance_property();

        for (const key in elements) {
            delete elements[key];
        }

        hide_controls_delayed.clear();
    }

    const instance = {
        play: play,
        pause: pause,
        toggle_play: toggle_play,
        mute: mute,
        unmute: unmute,
        toggle_mute: toggle_mute,
        rewind_video: rewind_video,
        destroy: destroy
    };

    constructor(instance);

    return instance;
}